import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import MasterEditModal from '../../components/MasterEditModal';
import { useAuth } from '../../context/AuthContext';

const roleOptions = [
  { value: 'super_admin', label: 'سوبر أدمن' },
  { value: 'admin', label: 'مدير عام' },
  { value: 'branch_admin', label: 'مدير مزاد' },
  { value: 'inspector', label: 'فاحص' },
  { value: 'viewer', label: 'مشاهد' },
];

const permissionOptions = [
  { value: 'manage_branches', label: 'إدارة المزادات' },
  { value: 'manage_users', label: 'إدارة المستخدمين' },
  { value: 'manage_master', label: 'إعدادات الترميز' },
  { value: 'create_inspections', label: 'إنشاء الفحوصات' },
  { value: 'view_reports', label: 'عرض التقارير' },
];

const defaultsByRole = {
  super_admin: permissionOptions.map((option) => option.value),
  admin: permissionOptions.map((option) => option.value),
  branch_admin: ['manage_users', 'create_inspections', 'view_reports'],
  inspector: ['create_inspections', 'view_reports'],
  viewer: ['view_reports'],
};

const emptyForm = {
  name: '',
  username: '',
  email: '',
  password: '',
  role: 'inspector',
  auction_ids: [],
  permissions: defaultsByRole.inspector,
  is_active: true,
};

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canPickAuction = ['super_admin', 'admin'].includes(user?.role);
  const visibleRoleOptions = ['super_admin', 'admin'].includes(user?.role)
    ? roleOptions
    : roleOptions.filter((option) => !['super_admin', 'admin'].includes(option.value));

  const load = useCallback(() => {
    Promise.all([api.get('/users'), api.get('/auctions')]).then(([usersRes, auctionsRes]) => {
      setRows(usersRes.data || []);
      setAuctions(auctionsRes.data || []);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'role') next.permissions = defaultsByRole[value] || [];
      return next;
    });
  };

  const add = async () => {
    if (!form.name.trim() || !form.username.trim() || !form.password.trim() || !Array.isArray(form.auction_ids) || !form.auction_ids.length) return;
    setError('');
    try {
      await api.post('/users', form);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذر إضافة المستخدم.');
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      name: row.name || '',
      username: row.username || '',
      email: row.email || '',
      password: '',
      role: row.role || 'inspector',
      auction_ids: Array.isArray(row.auction_ids) ? row.auction_ids : [],
      permissions: row.permissions || [],
      is_active: row.is_active,
    });
  };

  const update = async () => {
    if (!editRow || !editForm.name.trim() || !editForm.username.trim() || !Array.isArray(editForm.auction_ids) || !editForm.auction_ids.length) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`/users/${editRow.id}`, editForm);
      setEditRow(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذر حفظ المستخدم.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm('تأكيد حذف المستخدم؟')) return;
    setError('');
    try {
      await api.delete(`/users/${row.id}`);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذر حذف المستخدم.');
    }
  };

  const fields = [
    { name: 'name', label: 'اسم المستخدم', required: true },
    { name: 'username', label: 'اسم الدخول', required: true },
    { name: 'email', label: 'البريد الإلكتروني' },
    { name: 'password', label: 'كلمة المرور', type: 'password', placeholder: editRow ? 'اتركه فارغاً بدون تغيير' : '', required: !editRow },
    {
      name: 'role',
      label: 'الدور',
      type: 'select',
      required: true,
      options: visibleRoleOptions,
    },
    {
      name: 'permissions',
      label: 'الصلاحيات',
      type: 'multicheck',
      fullWidth: true,
      options: permissionOptions,
    },
    {
      name: 'auction_ids',
      label: 'المزادات المسموحة',
      type: 'multicheck',
      fullWidth: true,
      options: auctions.map((auction) => ({ value: auction.id, label: auction.name })),
    },
    { name: 'is_active', label: 'نشط', type: 'checkbox' },
  ];

  return (
    <div>
      <div className="grid gap-2 md:grid-cols-3 mb-3">
        <input value={form.name} onChange={(e) => setField('name', e.target.value)} className="rounded-lg border p-2" placeholder="اسم المستخدم" />
        <input value={form.username} onChange={(e) => setField('username', e.target.value)} className="rounded-lg border p-2" placeholder="اسم الدخول" />
        <input value={form.password} onChange={(e) => setField('password', e.target.value)} className="rounded-lg border p-2" placeholder="كلمة المرور" type="password" />
      </div>
      <div className="grid gap-2 md:grid-cols-3 mb-3">
        <select value={form.role} onChange={(e) => setField('role', e.target.value)} className="rounded-lg border p-2">
          {visibleRoleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <input value={form.email} onChange={(e) => setField('email', e.target.value)} className="rounded-lg border p-2" placeholder="البريد الإلكتروني" />
        <button onClick={add} className="rounded-lg bg-primary px-3 py-2 text-white">إضافة مستخدم</button>
      </div>

      <div className="mb-3 rounded-lg border bg-white p-3">
        <p className="mb-2 text-sm font-semibold text-slate-700">المزادات المسموحة</p>
        <div className="flex flex-wrap gap-2">
          {auctions.map((auction) => {
            const active = Array.isArray(form.auction_ids) && form.auction_ids.includes(auction.id);
            return (
              <button
                key={auction.id}
                type="button"
                onClick={() => {
                  const selected = Array.isArray(form.auction_ids) ? form.auction_ids : [];
                  const next = active
                    ? selected.filter((id) => id !== auction.id)
                    : [...selected, auction.id];
                  setField('auction_ids', next);
                }}
                className={`rounded-lg border px-3 py-2 text-sm ${active ? 'border-primary bg-red-50 text-primary' : 'border-slate-200 text-slate-700'}`}
              >
                {active ? '✓ ' : ''}
                {auction.name}
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <table className="w-full text-sm border rounded-lg overflow-hidden">
        <thead className="bg-slate-100">
          <tr>
            <th className="p-2 text-right">المستخدم</th>
            <th className="p-2 text-right">اسم الدخول</th>
            <th className="p-2 text-right">الدور</th>
            <th className="p-2 text-right">المزاد</th>
            <th className="p-2 text-right">الحالة</th>
            <th className="p-2">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="p-2">{row.name}</td>
              <td className="p-2">{row.username}</td>
              <td className="p-2">{roleOptions.find((option) => option.value === row.role)?.label || row.role}</td>
              <td className="p-2">{row.auction?.name || row.branch?.name || '-'}</td>
              <td className="p-2">{row.is_active ? 'نشط' : 'موقوف'}</td>
              <td className="p-2 flex gap-2 justify-center">
                <button onClick={() => openEdit(row)} className="px-2 py-1 border rounded">تعديل</button>
                {row.id !== user?.id && <button onClick={() => remove(row)} className="px-2 py-1 border rounded text-red-600">حذف</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <MasterEditModal
        open={Boolean(editRow)}
        title="تعديل المستخدم"
        subtitle="يمكن تغيير الدور والصلاحيات وربط المستخدم بالمزاد المتاح له."
        fields={fields}
        values={editForm}
        onChange={(key, value) => {
          setEditForm((current) => {
            const next = { ...current, [key]: value };
            if (key === 'role') next.permissions = defaultsByRole[value] || [];
            return next;
          });
        }}
        onClose={() => setEditRow(null)}
        onSubmit={update}
        saving={saving}
      />
    </div>
  );
}
